import type { GateMovementDirection } from '@/__generated__/graphql';
import { AU_SCROLLBAR_SX, AU_TEXT_DIM } from '@/theme/au-compat';
import { MovementDataRenderer } from '@features/gateCommon';
import { isFinalizedState } from '@features/gateCommon/utils/gateMovementFinalized';
import { Trans } from '@lingui/macro';
import { Box, Skeleton, Typography, type SxProps, type Theme } from '@mui/material';
import {
  type FC,
  memo,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { List, RowComponentProps, useListRef } from 'react-window';
import { PanelHeading } from './common';
import {
  feedItemKey,
  groupNotifications,
  type FeedItem,
} from './panelNotification/groupNotifications';
import { MovementGroupCard, NotificationCard } from './panelNotification/NotificationCard';
import { NotificationData } from './panelNotification/utils';

interface Props {
  movementId: number | null;
  /** Ajustes de layout del contenedor — hoy, el tope propio que el feed recupera apilado. */
  sx?: SxProps<Theme>;
}

/**
 * El alto de una fila: la tarjeta (56, que es lo que miden el glifo y sus dos líneas con el padding
 * de `.oc-notif`) más los 6px de aire de `.oc-notif-list`. `react-window` mide en filas de alto
 * fijo, así que el gap va **dentro** de la fila y no entre filas.
 */
const ROW_HEIGHT = 62;
const ROW_GAP = 6;

/**
 * Fade de los bordes del feed, la receta de `.au-scroll-viewport--fade`: cada borde se desvanece
 * hasta 24px y sólo cuando hay contenido por scrollear de ese lado. El desborde lo escribe
 * `NotificationList` en dos custom properties del elemento que scrollea.
 */
const FEED_FADE_MASK = `linear-gradient(
  to bottom,
  transparent 0,
  currentColor min(var(--feed-overflow-start, 0px), 24px),
  currentColor calc(100% - min(var(--feed-overflow-end, 0px), 24px)),
  transparent 100%
)`;

const NotificationRow: FC<
  RowComponentProps<{
    items: FeedItem[];
    direction: GateMovementDirection | null;
    animateFeaturedKey: number | null;
    onAnimationDone: () => void;
  }>
> = memo(
  ({ index, style, ariaAttributes, items, direction, animateFeaturedKey, onAnimationDone }) => {
    const item = items[index];
    if (!item) return null;
    const cardProps = {
      direction,
      isFeatured: index === 0,
      animate: index === 0 && feedItemKey(item) === animateFeaturedKey,
      onAnimationEnd: onAnimationDone,
    };
    return (
      /**
       * `ariaAttributes` se propaga: trae el `role="listitem"` y el `aria-posinset`/`aria-setsize` que
       * la lista virtualizada necesita para que el lector diga «3 de 47».
       */
      <Box style={style} {...ariaAttributes} sx={{ pb: `${ROW_GAP}px` }}>
        {item.kind === 'movement' ? (
          <MovementGroupCard group={item} {...cardProps} />
        ) : (
          <NotificationCard data={item.data} {...cardProps} />
        )}
      </Box>
    );
  },
);

const NotificationList: FC<{
  messages: NotificationData[];
  /** El movimiento terminó: congela el grupo de `MOVEMENT` aunque el cierre no traiga mensaje. */
  finished: boolean;
  direction: GateMovementDirection | null;
  labelledBy: string;
}> = ({ messages, finished, direction, labelledBy }) => {
  const items = useMemo(() => groupNotifications(messages, finished), [messages, finished]);

  const lastFeaturedKeyRef = useRef<number | null>(null);
  const [animateFeaturedKey, setAnimateFeaturedKey] = useState<number | null>(null);

  // Por la clave del ítem 0 y no por el id del último mensaje: un grupo que suma un reporte
  // conserva la clave de su primer reporte y se actualiza en el lugar, sin volver a entrar.
  const currentKey = items[0] ? feedItemKey(items[0]) : null;

  useEffect(() => {
    if (currentKey !== null && currentKey !== lastFeaturedKeyRef.current) {
      lastFeaturedKeyRef.current = currentKey;
      setAnimateFeaturedKey(currentKey);
    }
  }, [currentKey]);

  const onAnimationDone = useCallback(() => setAnimateFeaturedKey(null), []);

  const rowProps = useMemo(
    () => ({ items, direction, animateFeaturedKey, onAnimationDone }),
    [items, direction, animateFeaturedKey, onAnimationDone],
  );

  const listRef = useListRef(null);
  // Se escribe directo en el estilo del elemento: el scroll no tiene que re-renderizar la lista.
  const measureOverflow = useCallback(() => {
    const element = listRef.current?.element;
    if (!element) return;
    const start = element.scrollTop;
    const end = Math.max(0, element.scrollHeight - element.clientHeight - element.scrollTop);
    element.style.setProperty('--feed-overflow-start', `${start}px`);
    element.style.setProperty('--feed-overflow-end', `${end}px`);
  }, [listRef]);

  useLayoutEffect(measureOverflow, [measureOverflow, items.length]);

  return (
    /**
     * `role="log"` con `aria-relevant="additions"`: el lector anuncia solo la entrada nueva. Va en el
     * contenedor del scroll porque el root de `react-window` ya es `role="list"`.
     */
    <Box
      role="log"
      aria-live="polite"
      aria-relevant="additions"
      aria-labelledby={labelledBy}
      sx={{
        height: '100%',
        ...AU_SCROLLBAR_SX,
        '& > div': {
          ...AU_SCROLLBAR_SX,
          WebkitMaskImage: FEED_FADE_MASK,
          maskImage: FEED_FADE_MASK,
        },
      }}
    >
      <List
        listRef={listRef}
        rowCount={items.length}
        rowHeight={ROW_HEIGHT}
        rowComponent={NotificationRow}
        rowProps={rowProps}
        onScroll={measureOverflow}
        onResize={measureOverflow}
        style={{ overflowX: 'hidden' }}
      />
    </Box>
  );
};

/**
 * **El feed no es una card**, y por eso no usa `PanelPaper`: va directo sobre el cuerpo del
 * diálogo. Cada mensaje ya trae su propio fondo teñido, así que envolverlos deja las tarjetas dos
 * veces encajadas y el tono de cada una —que es el dato— compitiendo con la superficie que las
 * contiene.
 */
export const PanelNotifications: FC<Props> = ({ movementId, sx }) => {
  const headingId = useId();

  return (
    <Box
      sx={{
        gridArea: 'notif',
        minWidth: 0,
        minHeight: 0,
        px: '2px',
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
    >
      <PanelHeading id={headingId}>
        <Trans>Operación en tiempo real</Trans>
      </PanelHeading>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {movementId ? (
          <MovementDataRenderer
            movementId={movementId}
            render={({ movement }) => {
              const messages = movement.messages;
              if (messages.length === 0) {
                return (
                  <Typography
                    sx={{ fontSize: 12, fontStyle: 'italic', color: AU_TEXT_DIM, p: '12px 4px' }}
                  >
                    <Trans>Aún no se han recibido mensajes de la compuerta</Trans>
                  </Typography>
                );
              }
              return (
                <NotificationList
                  messages={messages}
                  finished={isFinalizedState(movement.state)}
                  direction={movement.direction ?? null}
                  labelledBy={headingId}
                />
              );
            }}
            // Alto completo: un `Skeleton` de una línea dejaba el área del feed casi vacía y el
            // diálogo entero se reacomodaba al llegar los mensajes.
            fallback={() => <Skeleton variant="rounded" sx={{ height: '100%' }} />}
          />
        ) : (
          <Typography sx={{ fontSize: 12, fontStyle: 'italic', color: AU_TEXT_DIM, p: '12px 4px' }}>
            <Trans>Aún no se han recibido mensajes de la compuerta</Trans>
          </Typography>
        )}
      </Box>
    </Box>
  );
};
